import { Application } from '@platform/client';
import { ReckittApplicationParameters } from '@platform/client/schemas';
import { ErrorBox, FormsInputDropdown } from '@platform/components';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ApplicationPageProps } from '../../Application';
import { useApplicationLocalStorage } from '../../application-hook';
import { ApplicationBackground } from '../../ApplicationBackground';
import { ApplicationPageContent } from '../../components/ApplicationPageContent';
import BackgroundImage from '../../images/Aanu-Gopald.svg';

type FormValues = {
  referralMethod: string;
};

const RECKITT_REFERRAL_OPTIONS = [
  { text: 'A colleague within your company', textValue: 'colleague' },
  { text: 'Your manager', textValue: 'manager' },
  { text: 'Your Global CSR leader', textValue: 'globalCSR' },
  { text: 'Your Regional CSR leader', textValue: 'regionalCSR' },
  { text: 'Email communication from your company', textValue: 'email' },
  { text: 'Internal meeting or town hall', textValue: 'meeting' },
  { text: 'LinkedIn', textValue: 'linkedin' },
  { text: 'Other', textValue: 'other' },
];

export const ReckittSubmission = (props: ApplicationPageProps) => {
  const { onBack, progress, dataKey } = { ...props };
  const [failed, setFailed] = useState(false);

  const { getApplicationData } = useApplicationLocalStorage<ReckittApplicationParameters>(dataKey);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ mode: 'onBlur', reValidateMode: 'onChange' });

  const submitReckittMutation = Application.useSubmitReckittApplication();
  const queryClient = useQueryClient();

  const submit: SubmitHandler<FormValues> = (data) => {
    submitReckittMutation.mutate(
      {
        data: {
          ...getApplicationData(),
          referralMethod: data.referralMethod,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: Application.getGetProfessionalApplicationStatusQueryKey(),
          });
        },
        onError: () => {
          setFailed(true);
        },
      },
    );
  };
  return (
    <ApplicationBackground
      progress={progress}
      onBack={onBack}
      onSubmit={handleSubmit(submit)}
      backgroundImage={BackgroundImage}
    >
      <ApplicationPageContent
        title='Ready to submit? Just one more thing.'
        description='After you submit, your responses are final, so take a moment to make sure everything is just right.'
      >
        <FormsInputDropdown
          control={control}
          name='referralMethod'
          label='How did you find out about this opportunity?'
          placeholder='Select one'
          items={RECKITT_REFERRAL_OPTIONS}
          type='default'
          error={errors.referralMethod}
          inputDisabled={true}
        />
        {failed && (
          <ErrorBox showAlertTriangle={true}>
            There was an error submitting your application. Please try again later.
          </ErrorBox>
        )}
      </ApplicationPageContent>
    </ApplicationBackground>
  );
};
